/**
 * Причина архивации поля — справочник, а не свободный текст (AK-746).
 *
 * Решение founder 25.08: причина ОБЯЗАТЕЛЬНА. Он в тот день заархивировал 13
 * полей руками, и через месяц по свободной строке уже не отличить «дубль
 * импорта» от «вернули арендодателю» — а именно по этому различию собирается
 * вкладка «Архив» и разбор отклонений сверки с «Историей поля».
 *
 * Поэтому причина едет двумя частями: КОД из списка (по нему потом
 * группируют) и свободный комментарий (по нему понимают конкретный случай).
 * В `fields.archive_reason` они складываются одной строкой `«код: коммент»` —
 * ручка AK-750 принимает текст, новых полей в схеме не заводим.
 */

/** Коды причин. Порядок — тот, в котором они стоят в списке. */
export const ARCHIVE_REASON_CODES = [
  'duplicate',
  'withdrawn',
  'merged',
  'input_error',
] as const;

export type ArchiveReasonCode = (typeof ARCHIVE_REASON_CODES)[number];

/** Подписи списка — языком агронома, без внутренних терминов. */
export const ARCHIVE_REASON_LABELS: Record<ArchiveReasonCode, string> = {
  duplicate: 'Дубль импорта — то же поле уже заведено',
  withdrawn: 'Земля выбыла из обработки',
  merged: 'Объединено с другим полем',
  input_error: 'Ошибка ввода',
};

/** Короткая подпись для колонки «Причина» во вкладке «Архив». */
export const ARCHIVE_REASON_SHORT: Record<ArchiveReasonCode, string> = {
  duplicate: 'Дубль',
  withdrawn: 'Выбыло',
  merged: 'Объединено',
  input_error: 'Ошибка ввода',
};

/**
 * Разбор ввода диалога — ТРИ исхода, а не два (канон «Ввод: пусто ≠ мусор»).
 *
 * Различие живёт в типе: пока функция возвращала бы `string | null`, «код не
 * выбран» и «всё хорошо, комментария нет» были бы одним значением, и пустая
 * причина уехала бы на сервер под зелёным тостом «Заархивировано».
 *
 * Комментарий НЕ обязателен: код уже отвечает на «почему», а требовать сверху
 * ещё и текст на каждом из 40 полей — это шлагбаум, а не забота.
 */
export function parseArchiveReason(
  code: ArchiveReasonCode | null,
  comment: string,
): { ok: true; value: string } | { ok: false; error: string } {
  if (!code) {
    return {
      ok: false,
      error: 'Выберите причину — по ней потом видно, почему поля нет',
    };
  }
  const trimmed = comment.trim();
  return { ok: true, value: trimmed ? `${code}: ${trimmed}` : code };
}

/**
 * Обратный разбор строки из БД для показа во вкладке «Архив».
 *
 * Старые записи причины не знают (архивация до AK-746 писала свободный текст
 * или вовсе ничего) — такие показываем как есть, а не выдумываем им код:
 * «Дубль» напротив поля, про которое мы этого не знаем, хуже прочерка.
 */
export function splitArchiveReason(raw: string | null | undefined): {
  code: ArchiveReasonCode | null;
  label: string;
  comment: string;
} {
  const value = (raw ?? '').trim();
  if (!value) return { code: null, label: '', comment: '' };
  const [head, ...rest] = value.split(':');
  const code = ARCHIVE_REASON_CODES.find((c) => c === head.trim());
  if (!code) return { code: null, label: value, comment: '' };
  return {
    code,
    label: ARCHIVE_REASON_SHORT[code],
    comment: rest.join(':').trim(),
  };
}
